import React, { useState, useEffect } from 'react';
import './BusinessInfo.css';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './firebase';
import { useGlobal } from './GlobalContext';
import { computeMissingFields } from './GlobalContext';
import ContactDetails from './ContactDetails';
import BusinessInfoTab from './BusinessInfoTab';
import InvoiceDetails from './InvoiceDetails';
import AdditionalDetails from './AdditionalDetails';
import Navbar from './components/Navbar';

const TABS = [
  { key: 'personalInfo',      label: 'Contact' },
  { key: 'businessInfo',      label: 'Club / School' },
  { key: 'invoiceDetails',    label: 'Invoice' },
  { key: 'additionalDetails', label: 'Schedule' },
];

const BusinessInfo = () => {
  const navigate = useNavigate();
  const { setIsBusinessInfoComplete, setMissingFields, refreshProfile } = useGlobal();

  const [activeTab, setActiveTab] = useState('personalInfo');

  const [fullName, setFullName] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [postCode, setPostCode] = useState('');
  const [venues, setVenues] = useState([{
    deliveryOption: 'pickup',
    collectorName: '',
    collectorEmail: '',
    collectorPhone: '',
    address: '',
    postCode: '',
    instructions: '',
    useMainContact: false,
    contactName: '',
    venueContactEmail: '',
    venueContactPhone: '',
  }]);
  const [clubTimings, setClubTimings] = useState({ from: '', to: '' });
  const [lunchTime, setLunchTime] = useState({ from: '', to: '' });
  const [deliveryWindow, setDeliveryWindow] = useState({ from: '', to: '' });
  const [ageRange, setAgeRange] = useState({ lower: '', upper: '' });
  const [isDeliveryWindowConfirmed, setIsDeliveryWindowConfirmed] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) return;
      getDoc(doc(db, 'ClientContactInfo', user.uid)).then((snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setFullName(d.fullName || '');
        setJobRole(d.jobRole || '');
        setContactEmail(d.contactEmail || '');
        setContactPhone(d.contactPhone || '');
        setBusinessName(d.businessName || '');
        setBusinessAddress(d.businessAddress || '');
        setPostCode(d.postCode || '');
        setClubTimings(d.clubTimings || { from: '', to: '' });
        setLunchTime(d.lunchTime || { from: '', to: '' });
        setDeliveryWindow(d.deliveryWindow || { from: '', to: '' });
        setAgeRange(d.ageRange || { lower: '', upper: '' });
        setIsDeliveryWindowConfirmed(d.isDeliveryWindowConfirmed || false);
        setLegalName(d.legalName || '');
        setLegalAddress(d.legalAddress || '');
        setInvoiceEmail(d.invoiceEmail || '');
        setConfirmationChecked(d.confirmationChecked || false);
        if (d.venues?.length) setVenues(d.venues);
      });
    });
    return () => unsubscribe();
  }, [auth, db]);

  const currentIndex = TABS.findIndex(t => t.key === activeTab);
  const goNext = () => currentIndex < TABS.length - 1 && setActiveTab(TABS[currentIndex + 1].key);
  const goPrev = () => currentIndex > 0 && setActiveTab(TABS[currentIndex - 1].key);

  const handleSaveChanges = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const user = auth.currentUser;
    if (!user) { alert('No user is signed in.'); return; }
    try {
      const newData = {
        fullName, jobRole, contactEmail, contactPhone,
        businessName, businessAddress, postCode, venues,
        clubTimings, lunchTime, deliveryWindow, ageRange,
        isDeliveryWindowConfirmed, legalName, legalAddress,
        invoiceEmail, confirmationChecked,
      };
      await httpsCallable(functions, 'saveBusinessInfo')(newData);

      // Recompute missing fields immediately
      const missing = computeMissingFields(newData);
      setMissingFields(missing);
      setIsBusinessInfoComplete(missing.length === 0);

      if (missing.length === 0) {
        alert('All information saved successfully! Your profile is complete.');
      } else {
        alert(`Saved! Still missing ${missing.length} field(s) — see the banner on the home screen.`);
      }
    } catch (err) {
      console.error(err);
      const detail = [err?.code, err?.message].filter(Boolean).join(': ');
      alert(`Failed to save. ${detail || 'Please try again.'}`);
    }
  };

  return (
    <div className="bi-page">
      <Navbar title="Business Information" />

      <div className="bi-wrapper">

        {/* Step indicator */}
        <div className="bi-steps">
          {TABS.map((tab, i) => (
            <button
              key={tab.key}
              className={`bi-step ${activeTab === tab.key ? 'active' : ''} ${i < currentIndex ? 'done' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="bi-step-num">{i < currentIndex ? '✓' : i + 1}</span>
              <span className="bi-step-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Progress bar */}
        <div className="bi-progress">
          <div className="bi-progress-fill" style={{ width: `${((currentIndex + 1) / TABS.length) * 100}%` }} />
        </div>

        {/* Form panels */}
        <div className="bi-panel">
          {activeTab === 'personalInfo' && (
            <ContactDetails
              fullName={fullName} setFullName={setFullName}
              jobRole={jobRole} setJobRole={setJobRole}
              contactEmail={contactEmail} setContactEmail={setContactEmail}
              contactPhone={contactPhone} setContactPhone={setContactPhone}
              onSave={handleSaveChanges} onNext={goNext}
            />
          )}
          {activeTab === 'businessInfo' && (
            <BusinessInfoTab
              businessName={businessName} setBusinessName={setBusinessName}
              businessAddress={businessAddress} setBusinessAddress={setBusinessAddress}
              postCode={postCode} setPostCode={setPostCode}
              venues={venues} setVenues={setVenues}
              fullName={fullName} contactEmail={contactEmail} contactPhone={contactPhone}
              onSave={handleSaveChanges} onPrev={goPrev} onNext={goNext}
            />
          )}
          {activeTab === 'invoiceDetails' && (
            <InvoiceDetails
              legalName={legalName} setLegalName={setLegalName}
              legalAddress={legalAddress} setLegalAddress={setLegalAddress}
              invoiceEmail={invoiceEmail} setInvoiceEmail={setInvoiceEmail}
              onSave={handleSaveChanges} onPrev={goPrev} onNext={goNext}
            />
          )}
          {activeTab === 'additionalDetails' && (
            <AdditionalDetails
              clubTimings={clubTimings} setClubTimings={setClubTimings}
              lunchTime={lunchTime} setLunchTime={setLunchTime}
              deliveryWindow={deliveryWindow} setDeliveryWindow={setDeliveryWindow}
              ageRange={ageRange} setAgeRange={setAgeRange}
              isDeliveryWindowConfirmed={isDeliveryWindowConfirmed}
              setIsDeliveryWindowConfirmed={setIsDeliveryWindowConfirmed}
              confirmationChecked={confirmationChecked}
              setConfirmationChecked={setConfirmationChecked}
              onSave={handleSaveChanges} onPrev={goPrev}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default BusinessInfo;
